/**
 * 大厅数据缓存 Store
 * 缓存轮播图、公告、厂牌、板块、游戏类型等不常变动的数据
 */

import { defineStore } from 'pinia'
import { getBanners, getAnnouncements, getProviders, getSections, getGameTypes } from '@/services/api/public'
import { getCurrencies } from '@/services/api/wallet'
import { getAccessToken } from '@/services/request'
import type { Banner, Announcement, ProviderInfo, CurrencyInfo, SectionInfo, GameTypeInfo } from '@/types/api'

// 缓存有效期（毫秒）- 5分钟
const CACHE_TTL = 5 * 60 * 1000

// 用户上次选择的活跃币种 (跨页面共享 + 跨会话持久化)
// 之前每个页面 (MyCenter/Transactions/AddBankCard/Promotions) 各自维护 selectedCurrency ref,
// 导致选择不互通; 现统一收口到 store.
const ACTIVE_CURRENCY_KEY = 'active_currency'

interface CategoryTab {
  key: string
  label: string
  type: 'fixed' | 'game_type'
}

interface LobbyState {
  banners: Banner[]
  announcements: Announcement[]
  providers: ProviderInfo[]
  currencies: CurrencyInfo[]
  sections: SectionInfo[]
  gameTypes: GameTypeInfo[]
  lastFetchTime: number | null
  loading: boolean
  // 活跃币种 (空串 = 尚未确定, 待 loadLobbyData 拉到 currencies 后用 codes[0] 兜底)
  activeCurrency: string
}

export const useLobbyStore = defineStore('lobby', {
  state: (): LobbyState => ({
    banners: [],
    announcements: [],
    providers: [],
    currencies: [],
    sections: [],
    gameTypes: [],
    lastFetchTime: null,
    loading: false,
    activeCurrency: localStorage.getItem(ACTIVE_CURRENCY_KEY) || '',
  }),

  getters: {
    // 缓存是否有效
    isCacheValid(): boolean {
      if (!this.lastFetchTime) return false
      return Date.now() - this.lastFetchTime < CACHE_TTL
    },

    // 币种代码列表（方便选择器使用）
    currencyCodes(): string[] {
      return this.currencies.map(c => c.code)
    },

    // 跑马灯文本（取第一条滚动公告）
    marqueeText(): string {
      const scrollAnnouncement = this.announcements.find(a => a.is_scroll)
      if (!scrollAnnouncement) return ''
      // content 兼容: JSON 标量字符串 / 多语言对象 / 原始串
      let text = scrollAnnouncement.content
      try {
        const v = JSON.parse(scrollAnnouncement.content)
        if (typeof v === 'string') text = v
        else if (v && typeof v === 'object') text = v.zh || v.en || scrollAnnouncement.content
      } catch {
        text = scrollAnnouncement.content
      }
      // 跑马灯为纯文本单行, 去掉富文本 HTML 标签
      return text.replace(/<[^>]+>/g, '').trim()
    },

    // 分类 Tab 列表: 3 个固定 + 动态游戏类型
    categoryTabs(): CategoryTab[] {
      const fixed: CategoryTab[] = [
        { key: 'home', label: '首页', type: 'fixed' },
        { key: 'hot', label: '热门', type: 'fixed' },
        { key: 'recent', label: '最近', type: 'fixed' },
      ]
      const dynamic: CategoryTab[] = this.gameTypes.map(gt => ({
        key: gt.code,
        label: parseGameTypeName(gt),
        type: 'game_type' as const,
      }))
      return [...fixed, ...dynamic]
    },
  },

  actions: {
    /**
     * 加载大厅数据（带缓存）
     * @param force 是否强制刷新（忽略缓存）
     */
    async loadLobbyData(force = false): Promise<void> {
      if (!force && this.isCacheValid) {
        return
      }
      if (this.loading) {
        return
      }

      this.loading = true

      // getCurrencies 要 token (auth: true), 未登录访客调它会触发 100010 "缺少Authorization头"
      // 其他 5 个接口都是公开接口, 未登录可调 → 用 null 占位让 Promise.all 形状不变
      const isAuthed = !!getAccessToken()

      // 6 个接口各自独立成败 (每个都自带 try/catch, 不会互相拖累/不会 reject Promise.all),
      // 但只要有一个失败, 就不能把这次结果当"成功"缓存起来——不然 isCacheValid 会在接下来
      // 5 分钟内一直挡掉重试, 首页对应板块 (厂商/热门等) 就会一直空着, 明明网络已经恢复了
      // 也要等缓存过期才会再拉一次。这正是"首页有时候不显示"的根因, 别再往这加字段时漏了这层判断。
      let allSucceeded = true

      try {
        const [bannersRes, announcementsRes, providersRes, currenciesRes, sectionsRes, gameTypesRes] = await Promise.all([
          getBanners(),
          getAnnouncements(),
          getProviders(),
          isAuthed ? getCurrencies() : Promise.resolve(null),
          getSections(),
          getGameTypes(),
        ])

        if (bannersRes.success && bannersRes.data) {
          const items = bannersRes.data.items || []
          this.banners = items
            .filter(b => b.status === 2)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        } else {
          allSucceeded = false
        }
        if (announcementsRes.success && announcementsRes.data) {
          this.announcements = announcementsRes.data.items || []
        } else {
          allSucceeded = false
        }
        if (providersRes.success && providersRes.data) {
          // 视讯厂牌 (code='huanhai') 现在后端已经真实返回, 不再需要前端占位注入
          this.providers = (providersRes.data || []).filter(p => p.status === 1)
        } else {
          allSucceeded = false
        }
        // currenciesRes 为 null 是未登录访客的预期情况 (故意不调), 不算失败
        if (currenciesRes) {
          if (currenciesRes.success && currenciesRes.data) {
            this.currencies = currenciesRes.data || []
            // 兜底: 用户从未选过 (空串) 或上次的币种已下线 → 用第一个
            const codes = this.currencies.map(c => c.code)
            if (codes.length > 0 && !codes.includes(this.activeCurrency)) {
              this.setActiveCurrency(codes[0] ?? '')
            }
          } else {
            allSucceeded = false
          }
        }
        if (sectionsRes.success && sectionsRes.data) {
          this.sections = sectionsRes.data || []
        } else {
          allSucceeded = false
        }
        if (gameTypesRes.success && gameTypesRes.data) {
          this.gameTypes = (gameTypesRes.data || []).sort((a, b) => a.sort_order - b.sort_order)
        } else {
          allSucceeded = false
        }

        if (allSucceeded) {
          this.lastFetchTime = Date.now()
        } else {
          console.warn('[LobbyStore] 部分接口加载失败, 不缓存本次结果, 下次调用会重新拉取')
        }
      } catch (error) {
        console.error('[LobbyStore] 加载数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    clearCache(): void {
      this.lastFetchTime = null
    },

    setActiveCurrency(code: string): void {
      this.activeCurrency = code
      if (code) localStorage.setItem(ACTIVE_CURRENCY_KEY, code)
      else localStorage.removeItem(ACTIVE_CURRENCY_KEY)
    },

    async refresh(): Promise<void> {
      await this.loadLobbyData(true)
    },
  },
})

/**
 * 解析游戏类型多语言名称
 * name_lang 为 JSON 格式 {"zh":"体育","en":"Sport",...}
 * fallback 到 name 字段
 */
function parseGameTypeName(gt: GameTypeInfo): string {
  if (gt.name_lang) {
    try {
      const lang = JSON.parse(gt.name_lang)
      return lang.zh || lang.en || gt.name
    } catch {
      // ignore
    }
  }
  return gt.name
}
